import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, KeyRound, Monitor, Smartphone, LogOut, ShieldCheck, RotateCw, ShieldAlert, Copy, Download, Check, Ban } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { copyText } from "@/lib/clipboard";
import { TextField } from "@/components/kit/fields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { TelegramIntegrationCard } from "@/components/settings/TelegramIntegrationCard";
import { fmtDate, fmtDateTime } from "@/lib/format";

const CARD = "rounded-xl border border-white/[0.08] bg-[#141417] p-5";

// Lightweight pub/sub so sibling cards react to 2FA enable/disable without a page reload.
const _twofaListeners = new Set();
function emit2faChange() { _twofaListeners.forEach((fn) => { try { fn(); } catch { /* noop */ } }); }
function useOn2faChange(cb) {
  useEffect(() => { _twofaListeners.add(cb); return () => _twofaListeners.delete(cb); }, [cb]);
}

export function ChangeEmailCard() {
  const { user, refresh } = useAuth();
  const [v, setV] = useState({ new_email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!v.new_email || !v.password) return toast.error("Fill in both fields");
    setSaving(true);
    try {
      await http.post("/auth/change-email", v);
      await refresh();
      setV({ new_email: "", password: "" });
      toast.success("Email updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to change email"); }
    finally { setSaving(false); }
  };
  return (
    <div className={CARD} data-testid="account-change-email">
      <div className="mb-3 flex items-center gap-2.5"><Mail className="h-4 w-4 text-muted-foreground" /><h3 className="text-sm font-medium text-foreground">Change email</h3></div>
      <p className="mb-3 text-xs text-muted-foreground">Current: <span className="text-foreground/80">{user?.email}</span></p>
      <div className="grid grid-cols-1 gap-3">
        <TextField label="New email" value={v.new_email} onChange={(x) => setV((s) => ({ ...s, new_email: x }))} testId="account-new-email" />
        <TextField label="Confirm with password" type="password" value={v.password} onChange={(x) => setV((s) => ({ ...s, password: x }))} testId="account-email-pass" />
      </div>
      <Button size="sm" onClick={save} disabled={saving} data-testid="account-email-save" className="mt-3 bg-white text-black hover:bg-white/90">Update email</Button>
    </div>
  );
}

export function ChangePasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.current_password || !form.new_password) return toast.error("Fill in both password fields");
    if (form.new_password.length < 6) return toast.error("New password must be at least 6 characters");
    if (form.new_password !== form.confirm) return toast.error("Passwords do not match");
    setSaving(true);
    try {
      const { data } = await http.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      setForm({ current_password: "", new_password: "", confirm: "" });
      const n = data?.sessions_revoked || 0;
      toast.success(n ? `Password changed · ${n} other session${n > 1 ? "s" : ""} signed out` : "Password changed");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to change password"); }
    finally { setSaving(false); }
  };
  return (
    <div className={CARD} data-testid="account-change-password">
      <div className="mb-3 flex items-center gap-2.5"><KeyRound className="h-4 w-4 text-muted-foreground" /><h3 className="text-sm font-medium text-foreground">Change password</h3></div>
      <div className="grid grid-cols-1 gap-4">
        <TextField label="Current password" type="password" value={form.current_password} onChange={(v) => set("current_password", v)} testId="account-current-pass" autoComplete="current-password" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField label="New password" type="password" value={form.new_password} onChange={(v) => set("new_password", v)} testId="account-new-pass" autoComplete="new-password" />
          <TextField label="Confirm new password" type="password" value={form.confirm} onChange={(v) => set("confirm", v)} testId="account-confirm-pass" autoComplete="new-password" />
        </div>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">Changing your password signs out all your other active sessions.</p>
      <Button onClick={save} disabled={saving} data-testid="account-password-save" className="mt-4 bg-white text-black hover:bg-white/90">Update password</Button>
    </div>
  );
}

function DeviceIcon({ device }) {
  const mobile = /iphone|android|mobile|phone|ipad|tablet/i.test(device || "");
  const Icon = mobile ? Smartphone : Monitor;
  return <Icon className="h-4 w-4 text-muted-foreground" />;
}

export function SessionsCard() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try { const { data } = await http.get("/auth/sessions"); setSessions(data || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const revoke = async (id) => {
    setBusy(id);
    try { await http.post(`/auth/sessions/${id}/revoke`); toast.success("Session ended"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };
  const revokeOthers = async () => {
    setBusy("others");
    try {
      const { data } = await http.post("/auth/sessions/revoke-others");
      toast.success(data?.sessions_revoked ? `Signed out ${data.sessions_revoked} session${data.sessions_revoked > 1 ? "s" : ""}` : "No other sessions");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };

  const others = sessions.filter((s) => !s.current).length;
  return (
    <div className={CARD} data-testid="account-sessions">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5"><ShieldCheck className="h-4 w-4 text-muted-foreground" /><h3 className="text-sm font-medium text-foreground">Active sessions</h3></div>
        <Button variant="ghost" size="sm" onClick={load} data-testid="sessions-refresh" className="h-7 gap-1.5 text-xs text-muted-foreground"><RotateCw className="h-3.5 w-3.5" /> Refresh</Button>
      </div>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : sessions.length === 0 ? (
        <p className="text-sm text-muted-foreground">No active sessions.</p>
      ) : (
        <div className="space-y-2">
          {sessions.map((s) => (
            <div key={s.id} data-testid={`session-row-${s.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-white/[0.06] bg-[#0A0A0A] px-3 py-2.5">
              <div className="flex items-center gap-3">
                <DeviceIcon device={s.device} />
                <div>
                  <p className="text-sm text-foreground">
                    {s.browser || "Unknown"} · {s.os || "Unknown"}
                    {s.current && <span className="ml-2 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-400">This device</span>}
                    {s.is_impersonation && <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-400">Impersonation</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {(s.ip || "—")}{s.country ? ` · ${s.country}` : ""} · last active {fmtDate(s.last_seen)}
                  </p>
                </div>
              </div>
              {!s.current && (
                <Button variant="outline" size="sm" disabled={busy === s.id} onClick={() => revoke(s.id)} data-testid={`session-revoke-${s.id}`} className="h-8 gap-1.5 border-white/15 text-[#EF4444]">
                  <LogOut className="h-3.5 w-3.5" /> End
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      {others > 0 && (
        <Button onClick={revokeOthers} disabled={busy === "others"} variant="outline" data-testid="sessions-revoke-others" className="mt-4 gap-2 border-white/15 text-[#EF4444]">
          <LogOut className="h-4 w-4" /> Sign out all other sessions
        </Button>
      )}
    </div>
  );
}

function RecoveryCodes({ codes }) {
  const [copied, setCopied] = useState(false);
  const copyAll = async () => { await copyText(codes.join("\n")); setCopied(true); toast.success("Recovery codes copied"); setTimeout(() => setCopied(false), 1500); };
  const download = () => {
    const blob = new Blob([`Tracify recovery codes\n\n${codes.join("\n")}\n`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "tracify-recovery-codes.txt"; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div data-testid="recovery-codes">
      <div className="grid grid-cols-2 gap-2 rounded-lg border border-white/[0.08] bg-[#0A0A0A] p-4 font-mono text-sm text-foreground">
        {codes.map((c) => <span key={c} data-testid="recovery-code">{c}</span>)}
      </div>
      <div className="mt-3 flex gap-2">
        <Button variant="outline" size="sm" onClick={copyAll} data-testid="recovery-copy" className="gap-1.5 border-white/15">{copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} Copy</Button>
        <Button variant="outline" size="sm" onClick={download} data-testid="recovery-download" className="gap-1.5 border-white/15"><Download className="h-3.5 w-3.5" /> Download</Button>
      </div>
    </div>
  );
}

export function TwoFactorCard({ embedded = false }) {
  const { refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  // setup dialog
  const [setupOpen, setSetupOpen] = useState(false);
  const [setup, setSetup] = useState(null); // {qr_data_url, secret, otpauth_uri}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [newCodes, setNewCodes] = useState(null);
  // password-gated dialogs
  const [pwDialog, setPwDialog] = useState(null); // "disable" | "regen"
  const [pw, setPw] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await http.get("/auth/2fa/status"); setStatus(data); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/auth/2fa/setup/initiate");
      setSetup(data); setCode(""); setNewCodes(null); setSetupOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start setup"); }
    finally { setBusy(false); }
  };
  const confirmSetup = async () => {
    if (!code.trim()) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      const { data } = await http.post("/auth/2fa/setup/confirm", { code: code.trim() });
      setNewCodes(data.recovery_codes);
      toast.success("Two-factor authentication enabled");
      await load(); await refresh(); emit2faChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
    finally { setBusy(false); }
  };
  const closeSetup = () => { setSetupOpen(false); setSetup(null); setCode(""); setNewCodes(null); };

  const submitPw = async () => {
    if (!pw) return toast.error("Enter your password");
    setBusy(true);
    try {
      if (pwDialog === "disable") {
        await http.post("/auth/2fa/disable", { password: pw });
        toast.success("Two-factor authentication disabled");
        setPwDialog(null); setPw(""); await load(); await refresh(); emit2faChange();
      } else {
        const { data } = await http.post("/auth/2fa/recovery-codes/regenerate", { password: pw });
        setNewCodes(data.recovery_codes); setPwDialog(null); setPw("");
        setSetup({}); setSetupOpen(true); // reuse dialog to show fresh codes
        toast.success("Recovery codes regenerated"); await load();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const enabled = status?.enabled;
  return (
    <div className={embedded ? "" : CARD} data-testid="account-2fa">
      <div className="mb-3 flex items-center gap-2.5"><ShieldAlert className="h-4 w-4 text-muted-foreground" /><h3 className="text-sm font-medium text-foreground">Two-factor authentication</h3></div>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-foreground">
              {enabled ? <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-400" data-testid="twofa-status-enabled">Enabled</span>
                       : <span className="rounded-full bg-white/[0.06] px-2 py-0.5 text-xs font-medium text-muted-foreground" data-testid="twofa-status-disabled">Disabled</span>}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              {enabled
                ? <>Enabled {status.confirmed_at ? fmtDate(status.confirmed_at) : ""} · {status.recovery_codes_remaining} recovery code{status.recovery_codes_remaining === 1 ? "" : "s"} left</>
                : "Protect your account with an authenticator app (Google Authenticator, Authy, 1Password, etc.)."}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            {enabled ? (
              <>
                <Button variant="outline" size="sm" onClick={() => { setPwDialog("regen"); setPw(""); }} data-testid="twofa-regen" className="border-white/15">Regenerate codes</Button>
                <Button variant="outline" size="sm" onClick={() => { setPwDialog("disable"); setPw(""); }} data-testid="twofa-disable" className="border-white/15 text-[#EF4444]">Disable</Button>
              </>
            ) : (
              <Button size="sm" onClick={startSetup} disabled={busy} data-testid="twofa-enable" className="bg-white text-black hover:bg-white/90">Enable 2FA</Button>
            )}
          </div>
        </div>
      )}
      {!loading && enabled && (
        <div className="mt-4 flex items-center justify-between gap-4 rounded-lg border border-white/[0.06] bg-[#0A0A0A] px-3 py-2.5">
          <div>
            <div className="text-sm text-foreground">Require 2FA at login</div>
            <div className="text-xs text-muted-foreground">When off, 2FA stays available for password recovery but is not asked at sign-in.</div>
          </div>
          <Switch checked={!!status?.login_enabled} data-testid="twofa-login-toggle"
            onCheckedChange={async (v) => {
              try { await http.put("/auth/2fa/login-enforcement", { enabled: v }); await load(); toast.success(v ? "2FA required at login" : "2FA no longer required at login"); }
              catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
            }} />
        </div>
      )}

      {/* Setup / recovery-codes dialog */}
      <Dialog open={setupOpen} onOpenChange={(o) => { if (!o) closeSetup(); }}>
        <DialogContent className="border-white/[0.08] bg-[#141417]" data-testid="twofa-setup-dialog">
          {newCodes ? (
            <>
              <DialogHeader>
                <DialogTitle>Save your recovery codes</DialogTitle>
                <DialogDescription>Store these in a safe place. Each code works once and they are shown only now.</DialogDescription>
              </DialogHeader>
              <RecoveryCodes codes={newCodes} />
              <DialogFooter>
                <Button onClick={closeSetup} data-testid="twofa-done" className="bg-white text-black hover:bg-white/90">I've saved them</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Set up two-factor authentication</DialogTitle>
                <DialogDescription>Scan the QR code with your authenticator app, then enter the 6-digit code to confirm.</DialogDescription>
              </DialogHeader>
              <div className="flex flex-col items-center gap-4">
                {setup?.qr_data_url && <img src={setup.qr_data_url} alt="2FA QR code" data-testid="twofa-qr" className="h-44 w-44 rounded-lg bg-white p-2" />}
                {setup?.secret && (
                  <div className="w-full text-center">
                    <p className="text-xs text-muted-foreground">Or enter this key manually</p>
                    <button type="button" onClick={() => { copyText(setup.secret); toast.success("Key copied"); }} data-testid="twofa-secret" className="mt-1 font-mono text-sm tracking-wider text-foreground hover:underline">{setup.secret}</button>
                  </div>
                )}
                <div className="w-full space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Verification code</Label>
                  <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" inputMode="numeric" data-testid="twofa-setup-code" className="border-white/[0.1] bg-[#0A0A0A] tracking-widest focus-visible:ring-white/20" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeSetup} className="border-white/15">Cancel</Button>
                <Button onClick={confirmSetup} disabled={busy} data-testid="twofa-setup-confirm" className="bg-white text-black hover:bg-white/90">Confirm & enable</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Password-gated dialog (disable / regenerate) */}
      <Dialog open={!!pwDialog} onOpenChange={(o) => { if (!o) { setPwDialog(null); setPw(""); } }}>
        <DialogContent className="border-white/[0.08] bg-[#141417]" data-testid="twofa-password-dialog">
          <DialogHeader>
            <DialogTitle>{pwDialog === "disable" ? "Disable two-factor authentication" : "Regenerate recovery codes"}</DialogTitle>
            <DialogDescription>
              {pwDialog === "disable"
                ? "This removes your TOTP secret, all recovery codes and trusted devices. Confirm with your password."
                : "Your old recovery codes stop working. Confirm with your password."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Password</Label>
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="twofa-password-input" autoComplete="current-password" className="border-white/[0.1] bg-[#0A0A0A] focus-visible:ring-white/20" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPwDialog(null); setPw(""); }} className="border-white/15">Cancel</Button>
            <Button onClick={submitPw} disabled={busy} data-testid="twofa-password-confirm" className={pwDialog === "disable" ? "bg-[#EF4444] text-white hover:bg-[#EF4444]/90" : "bg-white text-black hover:bg-white/90"}>
              {pwDialog === "disable" ? "Disable" : "Regenerate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function TrustedAccessCard() {
  const [devices, setDevices] = useState([]);
  const [ips, setIps] = useState([]);
  const [newIp, setNewIp] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const st = await http.get("/auth/2fa/status");
      setEnabled(!!st.data?.enabled);
      if (st.data?.enabled) {
        const [d, i] = await Promise.all([http.get("/auth/2fa/devices"), http.get("/auth/2fa/trusted-ips")]);
        setDevices(d.data || []);
        setIps(i.data?.ips || []);
      }
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useOn2faChange(load);

  const removeDevice = async (id) => {
    setBusy(true);
    try { await http.post(`/auth/2fa/devices/${id}/remove`); toast.success("Device removed"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const removeAll = async () => {
    setBusy(true);
    try { await http.post("/auth/2fa/devices/remove-all"); toast.success("All trusted devices removed"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const saveIps = async (next) => {
    setBusy(true);
    try { const { data } = await http.put("/auth/2fa/trusted-ips", { ips: next }); setIps(data.ips || []); toast.success("Trusted IPs updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const addIp = () => {
    const v = newIp.trim();
    if (!v) return;
    if (ips.includes(v)) { setNewIp(""); return; }
    saveIps([...ips, v]); setNewIp("");
  };
  const removeIp = (ip) => saveIps(ips.filter((x) => x !== ip));

  if (loading || !enabled) return null;
  return (
    <div className={CARD} data-testid="account-trusted-access">
      <div className="mb-3 flex items-center gap-2.5"><Monitor className="h-4 w-4 text-muted-foreground" /><h3 className="text-sm font-medium text-foreground">Trusted devices &amp; IPs</h3></div>

      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-medium text-muted-foreground">Devices that skip 2FA (30 days)</p>
        {devices.length > 0 && <Button variant="ghost" size="sm" disabled={busy} onClick={removeAll} data-testid="trusted-devices-clear" className="h-7 text-xs text-[#EF4444]">Remove all</Button>}
      </div>
      {devices.length === 0 ? (
        <p className="text-sm text-muted-foreground">No trusted devices. Check "Trust this device" at 2FA login to add one.</p>
      ) : (
        <div className="space-y-2">
          {devices.map((d) => (
            <div key={d.id} data-testid={`trusted-device-${d.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-white/[0.06] bg-[#0A0A0A] px-3 py-2.5">
              <div className="flex items-center gap-3">
                <DeviceIcon device={d.os} />
                <div>
                  <p className="text-sm text-foreground">{d.label}{d.current && <span className="ml-2 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-400">This device</span>}</p>
                  <p className="text-xs text-muted-foreground">{(d.ip || "—")}{d.country ? ` · ${d.country}` : ""} · last used {fmtDate(d.last_used)}</p>
                </div>
              </div>
              <Button variant="outline" size="sm" disabled={busy} onClick={() => removeDevice(d.id)} data-testid={`trusted-device-remove-${d.id}`} className="h-8 border-white/15 text-[#EF4444]">Remove</Button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 border-t border-white/[0.06] pt-5">
        <p className="mb-2 text-xs font-medium text-muted-foreground">Trusted IP addresses (skip 2FA)</p>
        <div className="flex gap-2">
          <Input value={newIp} onChange={(e) => setNewIp(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addIp())} placeholder="e.g. 203.0.113.5" data-testid="trusted-ip-input" className="border-white/[0.1] bg-[#0A0A0A] focus-visible:ring-white/20" />
          <Button variant="outline" onClick={addIp} disabled={busy} data-testid="trusted-ip-add" className="border-white/15">Add</Button>
        </div>
        {ips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {ips.map((ip) => (
              <span key={ip} data-testid={`trusted-ip-${ip}`} className="flex items-center gap-1.5 rounded-full border border-white/[0.1] bg-[#0A0A0A] px-3 py-1 text-xs text-foreground">
                {ip}
                <button onClick={() => removeIp(ip)} data-testid={`trusted-ip-remove-${ip}`} className="text-muted-foreground hover:text-[#EF4444]">×</button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function TwoFactorAdminControl({ userId, userName }) {
  const [state, setState] = useState(null); // {enabled, confirmed_at}
  const [hidden, setHidden] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    try { const { data } = await http.get(`/auth/2fa/admin/${userId}`); setState(data); }
    catch { setHidden(true); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const reset = async () => {
    setBusy(true);
    try {
      await http.post(`/auth/2fa/admin/${userId}/reset`);
      toast.success("Two-factor authentication reset");
      setConfirm(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  if (hidden || !state) return null;
  return (
    <div className="space-y-3" data-testid="user-2fa-admin">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><ShieldAlert className="h-3.5 w-3.5" /> Two-factor authentication</div>
      <div className="flex items-center justify-between rounded-xl border border-white/[0.08] bg-[#0A0A0A] p-4">
        <div>
          <p className="text-sm text-foreground">
            {state.enabled
              ? <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-400" data-testid="user-2fa-enabled">Enabled</span>
              : <span className="rounded-full bg-white/[0.06] px-2 py-0.5 text-xs font-medium text-muted-foreground" data-testid="user-2fa-disabled">Disabled</span>}
          </p>
          <p className="mt-1.5 text-xs text-muted-foreground">
            {state.enabled
              ? <>Enabled {state.confirmed_at ? fmtDate(state.confirmed_at) : ""}. Resetting removes the TOTP secret, recovery codes and trusted devices — the user re-enrolls on next login.</>
              : "This user has not enabled 2FA."}
          </p>
        </div>
        {state.enabled && (
          <Button variant="outline" size="sm" onClick={() => setConfirm(true)} data-testid="user-2fa-reset" className="shrink-0 border-white/15 text-[#EF4444]">Reset 2FA</Button>
        )}
      </div>
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="border-white/[0.08] bg-[#141417]" data-testid="user-2fa-reset-dialog">
          <DialogHeader>
            <DialogTitle>Reset two-factor authentication</DialogTitle>
            <DialogDescription>This removes {userName ? <b>{userName}</b> : "the user"}'s TOTP secret, all recovery codes and trusted devices, and signs out their sessions. They will set up 2FA again on next login.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)} className="border-white/15">Cancel</Button>
            <Button onClick={reset} disabled={busy} data-testid="user-2fa-reset-confirm" className="bg-[#EF4444] text-white hover:bg-[#EF4444]/90">Reset 2FA</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function PasswordRecoveryCard() {
  const [s, setS] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await http.get("/auth/recovery/settings"); setS(data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  useOn2faChange(load);

  const toggle = async (key, val) => {
    try { const { data } = await http.put("/auth/recovery/settings", { [key]: val }); setS(data); toast.success("Recovery methods updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const Row = ({ id, title, desc, checked, locked, available, onChange, unavailableHint }) => (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-white/[0.06] bg-[#0A0A0A] px-3 py-2.5">
      <div className="min-w-0">
        <div className="text-sm text-foreground">{title}{locked && <span className="ml-2 rounded-full bg-white/[0.06] px-1.5 py-0.5 text-[10px] text-muted-foreground">always required</span>}</div>
        <div className="text-xs text-muted-foreground">{available === false ? unavailableHint : desc}</div>
      </div>
      {locked ? (
        <span className="flex items-center gap-1 text-xs font-medium text-emerald-400" data-testid={`recovery-method-${id}-locked`}><Check className="h-3.5 w-3.5" /> On</span>
      ) : (
        <Switch checked={checked} disabled={available === false} data-testid={`recovery-method-${id}`} onCheckedChange={onChange} />
      )}
    </div>
  );

  return (
    <div className={CARD} data-testid="password-recovery-card">
      <div className="mb-1 flex items-center gap-2.5"><ShieldCheck className="h-4 w-4 text-muted-foreground" /><h3 className="text-sm font-medium text-foreground">Password recovery</h3></div>
      <p className="mb-3 text-xs text-muted-foreground">Choose which checks are required to reset your password. Every enabled method must be passed — the more you enable, the safer your account.</p>
      {!s ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <div className="space-y-2">
          <Row id="email" title="Email" desc="A 6-digit code is sent to your account email." locked />
          <Row id="telegram" title="Telegram" desc="A 6-digit code is sent to your connected Telegram bot."
            checked={s.telegram.enabled} available={s.telegram.available}
            unavailableHint="Connect Telegram in the Telegram integration card below to enable this."
            onChange={(v) => toggle("telegram", v)} />
          <Row id="totp" title="Authenticator app" desc="A code from your authenticator app is required."
            checked={s.totp.enabled} available={s.totp.available}
            unavailableHint="Enable two-factor authentication above to use this."
            onChange={(v) => toggle("totp", v)} />
        </div>
      )}
    </div>
  );
}

export function AccountSecurity() {
  return (
    <div className="max-w-4xl space-y-4" data-testid="account-security">
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
        <ChangeEmailCard />
        <ChangePasswordCard />
      </div>
      {/* Authentication: 2FA + Telegram grouped — both protect sign-in and power recovery. */}
      <div className={CARD} data-testid="authentication-section">
        <div className="mb-1 flex items-center gap-2.5"><ShieldCheck className="h-4 w-4 text-muted-foreground" /><h3 className="text-sm font-medium text-foreground">Authentication</h3></div>
        <p className="mb-4 text-xs text-muted-foreground">Extra factors that protect your sign-in, deliver security alerts and unlock account recovery.</p>
        <TwoFactorCard embedded />
        <div className="my-4 border-t border-white/[0.06]" />
        <TelegramIntegrationCard embedded />
      </div>
      <PasswordRecoveryCard />
      <TrustedAccessCard />
      <SessionsCard />
      <BlockedIPsCard />
    </div>
  );
}

const _sourceLabel = (s) => (s === "telegram" ? "Telegram" : s === "manual" ? "Manual" : (s || "—"));

export function BlockedIPsCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await http.get("/security/ip-blocks"); setRows(data?.items || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const unblock = async (id, ip) => {
    setBusy(id);
    try { await http.delete(`/security/ip-blocks/${id}`); toast.success(`IP ${ip} unblocked`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className={CARD} data-testid="blocked-ips-card">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5"><Ban className="h-4 w-4 text-muted-foreground" /><h3 className="text-sm font-medium text-foreground">Blocked IPs</h3></div>
        <Button variant="ghost" size="sm" onClick={load} data-testid="blocked-ips-refresh" className="h-7 gap-1.5 text-xs text-muted-foreground"><RotateCw className="h-3.5 w-3.5" /> Refresh</Button>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        These IP addresses cannot sign in to your account. Blocks are personal — they only affect
        your login, not the whole platform. Add IPs via the <span className="text-foreground">Block IP</span> button in Telegram login alerts.
      </p>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No blocked IPs.</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-white/[0.06]">
          <table className="w-full" data-testid="blocked-ips-table">
            <thead className="border-b border-white/[0.06] bg-white/[0.02]">
              <tr>
                {["IP", "Blocked at", "Device", "Reason", "Source", ""].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`blocked-ip-row-${r.id}`} className="hover:bg-white/[0.02]">
                  <td className="whitespace-nowrap px-3 py-2.5 font-mono text-sm text-foreground">{r.ip}{r.country ? <span className="ml-2 text-xs text-muted-foreground">· {r.country}</span> : null}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-sm text-muted-foreground">{fmtDateTime(r.created_at)}</td>
                  <td className="px-3 py-2.5 text-sm text-muted-foreground">{[r.browser, r.os, r.device].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="px-3 py-2.5 text-sm text-muted-foreground">{r.reason || "—"}</td>
                  <td className="px-3 py-2.5 text-sm text-muted-foreground">{_sourceLabel(r.source)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <Button variant="outline" size="sm" disabled={busy === r.id} onClick={() => unblock(r.id, r.ip)} data-testid={`blocked-ip-unblock-${r.id}`} className="h-8 border-white/15">Unblock</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
